import React from 'react';
import {
  Button,
  ButtonToolbar,
  DropdownButton,
  MenuItem,
} from 'react-bootstrap';
import { getOrgTermsContent } from '../../../content/utils.js';
import SubHeader from '../../SubHeader.jsx';
import ChangeImpactsOverview from './ChangeImpactsOverview/ChangeImpactsOverview.jsx';
import ChangeImpactTable from './ChangeImpactTable.jsx';
import {
  navigateToChangeImpactDataImport,
  NewChangeImpactLink,
} from '../../links/index.jsx';
import Empty from '../../Empty.jsx';
import { PlusButtonIcon } from '../../icons/PlusButtonIcon.jsx';
import { changeImpactContent, noResultsContent } from '../../EmptyStates.jsx';
import DownloadReportIcon from '../../icons/DownloadReportIcon.jsx';
import ToggleBlock from '../../ToggleBlock.jsx';
import ProjectDetailsHeader from '../../headers/PageHeaders/Project/ProjectDetailsHeader.jsx';
import CalendarHeatmap from '../../reports/ChangeImpactReports/ChangeImpactHeatMap/index.jsx';
import ReportTypeSelect from '../../reports/ChangeImpactReports/ReportTypeSelect.jsx';
import ReportFilters from './ReportFilters/index.jsx';
import {
  sortBy,
  sortSearchResults,
} from '../../../containers/reports/utils/index.js';
import { getOrgSlug } from '../../../lib/utils.js';
import { UploadIcon } from '../../icons/UploadIcon.jsx';
import CiImportDataError from './CiImportDataError.jsx';
import CustomMenuItem from '../../CustomMenuItem.jsx';

function ChangeImpact({
  query,
  project,
  portfolio,
  searchSuccess,
  results,
  projectStatuses,
  onDownloadReport,
  permissions,
  isLoading,
  pieTypeRef,
  pieScaleRef,
  piePerceptionRef,
  changeImpactTypes,
  sortType,
  setSortType,
  viewType,
  setViewType,
  onDelete,
}) {
  const handleSelect = (e) => {
    setViewType(e);
  };
  const EmptyState = (
    <Empty content={changeImpactContent} addBtn>
      {project.manageProjectEntities ? (
        project.archived ? (
          <Button
            onClick={() =>
              window.alert(getOrgTermsContent('archivedProjectMessage'))
            }
            bsStyle="success"
          >
            <PlusButtonIcon />
            Add Change Impact
          </Button>
        ) : (
          <NewChangeImpactLink projectId={project.id}>
            <Button bsStyle="success">
              <PlusButtonIcon />
              Add Change Impact
            </Button>
          </NewChangeImpactLink>
        )
      ) : null}
    </Empty>
  );

  return (
    <React.Fragment>
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Change Impacts</h2>
          <strong className="header-item bordered text-muted">
            Total:&nbsp;
            <span className="text-primary">{results.length}</span>
          </strong>
        </div>
        <div className="flex">
          <ReportTypeSelect reportType={viewType} onSelect={handleSelect} />

          {project.manageProjectEntities ? (
            project.archived ? (
              <Button
                onClick={() =>
                  window.alert(getOrgTermsContent('archivedProjectMessage'))
                }
                bsStyle="success"
              >
                <PlusButtonIcon />
                Add Change Impact
              </Button>
            ) : (
              <NewChangeImpactLink projectId={project.id}>
                <Button bsStyle="success">
                  <PlusButtonIcon />
                  Add Change Impact
                </Button>
              </NewChangeImpactLink>
            )
          ) : null}
          <div className="actions m-l-16">
            <DropdownButton
              noCaret
              title=""
              id="id"
              className="btn-more btn btn-default"
            >
              {viewType !== 'heat' &&
                (results.length > 0 || Object.values(query).some(Boolean)) && (
                  <React.Fragment>
                    {viewType === 'pie' && ( // For pie
                      <React.Fragment>
                        <CustomMenuItem>
                          <button
                            type="button"
                            className="flex flex-align-center w-100 h-100 p-0 btn-clear bg-transparent"
                            disabled={isLoading}
                            onClick={onDownloadReport}
                          >
                            <DownloadReportIcon />
                            &nbsp;
                            <span className="m-l-8">
                              {isLoading ? 'Loading...' : 'Download PDF'}
                            </span>
                          </button>
                        </CustomMenuItem>
                        <CustomMenuItem divider />
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
              {project.manageProjectEntities ? (
                <CustomMenuItem>
                  <button
                    type="button"
                    className="flex flex-align-center w-100 h-100 p-0 btn-clear bg-transparent"
                    onClick={() =>
                      navigateToChangeImpactDataImport({
                        slug: getOrgSlug(),
                        projectId: String(project.id),
                      })
                    }
                  >
                    <UploadIcon />
                    &nbsp;
                    <span className="m-l-8">Data Import</span>
                  </button>
                </CustomMenuItem>
              ) : null}
            </DropdownButton>
          </div>
        </div>
      </SubHeader>
      {project && projectStatuses ? (
        <ProjectDetailsHeader
          portfolio={portfolio}
          project={project}
          projectStatuses={projectStatuses}
        />
      ) : null}

      <CiImportDataError project={project} />

      {results.length || Object.values(query).some((val) => !!val) ? (
        <React.Fragment>
          <ToggleBlock title="Filters" showOnStart>
            <ReportFilters
              query={query}
              projectId={project.id}
              resetDependencies={[viewType]}
              permissions={permissions}
            />
          </ToggleBlock>
          {viewType === 'heat' ? (
            <CalendarHeatmap
              project={project}
              results={results}
              query={query}
            />
          ) : (
            <ChangeImpactsOverview
              pieTypeRef={pieTypeRef}
              pieScaleRef={pieScaleRef}
              piePerceptionRef={piePerceptionRef}
              results={results}
              changeImpactTypes={changeImpactTypes}
            />
          )}
          <SubHeader flexJustify small>
            <div className="flex">
              <h2>Change Impacts Table</h2>
              <strong className="header-item bordered text-muted">
                Total:&nbsp;
                <span className="text-primary">{results.length}</span>
              </strong>
            </div>

            <div className="flex">
              <div className="flex">
                <div className="text-muted m-r-16">Sort by</div>
                <ButtonToolbar style={{ width: '250px' }}>
                  <DropdownButton
                    bsStyle="info"
                    id="sort-type"
                    title={sortType}
                    className="btn-block text-capitalize"
                    onSelect={(val) => setSortType(val)}
                  >
                    {sortBy.map((type, index) => (
                      <MenuItem
                        key={index}
                        active={sortType === type}
                        eventKey={type}
                      >
                        <span className="text-capitalize">{type}</span>
                      </MenuItem>
                    ))}
                  </DropdownButton>
                </ButtonToolbar>
              </div>

              <div className="m-l-16">
                {project.manageProjectEntities ? (
                  project.archived ? (
                    <Button
                      onClick={() =>
                        window.alert(
                          getOrgTermsContent('archivedProjectMessage')
                        )
                      }
                      bsStyle="success"
                    >
                      <PlusButtonIcon />
                      Add Change Impact
                    </Button>
                  ) : (
                    <NewChangeImpactLink projectId={project.id}>
                      <Button bsStyle="success">
                        <PlusButtonIcon />
                        Add Change Impact
                      </Button>
                    </NewChangeImpactLink>
                  )
                ) : null}
              </div>
            </div>
          </SubHeader>
        </React.Fragment>
      ) : null}

      {!results.length && Object.values(query).some((val) => !!val) ? (
        <Empty simplified content={noResultsContent} />
      ) : null}

      <div style={{ opacity: !searchSuccess ? 0.5 : 1 }}>
        {results.length ? (
          <ChangeImpactTable
            changeImpacts={sortSearchResults(results, sortType)}
            projectId={project.id}
            manageProjectEntities={project.manageProjectEntities}
            archived={project.archived}
            onDelete={onDelete}
          />
        ) : null}
        {!results.length && !Object.values(query).some((val) => !!val)
          ? EmptyState
          : null}
      </div>
    </React.Fragment>
  );
}

export default ChangeImpact;
